import React from 'react';
import type { CustomFieldConfig } from './CustomFieldsBuilder';

interface CustomFieldsDetailProps {
  customFields: Record<string, any>;
  fieldsConfig: CustomFieldConfig[];
}

export function formatCustomFieldValue(
  value: any,
  fieldType: CustomFieldConfig['fieldType']
): string {
  if (value === undefined || value === null || value === '') return '-';

  switch (fieldType) {
    case 'checkbox':
      return value ? 'Sim' : 'Não';
    case 'date': {
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('pt-BR');
    }
    case 'number':
      return typeof value === 'number' ? value.toLocaleString('pt-BR') : String(value);
    case 'select':
    case 'text':
    case 'textarea':
    default:
      return String(value);
  }
}

function renderRepeatGroupDetail(
  groupData: any[],
  config: CustomFieldConfig
): React.ReactNode {
  if (!Array.isArray(groupData) || groupData.length === 0 || !config.repeatGroup) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-foreground">
        {config.repeatGroup.groupLabel} ({groupData.length} {groupData.length === 1 ? 'item' : 'itens'})
      </span>
      {groupData.map((item, idx) => (
        <div key={idx} className="ml-4 p-2 bg-muted/50 rounded-ds-card border border-border">
          <span className="text-xs font-medium text-muted-foreground mb-1 block">
            {config.repeatGroup!.groupLabel} {idx + 1}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {config.repeatGroup!.fields.map((subField) => (
              <div key={subField.fieldKey} className="text-sm">
                <span className="text-muted-foreground">{subField.label}: </span>
                <span className="text-foreground">
                  {formatCustomFieldValue(item[subField.fieldKey], subField.fieldType)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function formatCustomFieldInline(
  value: any,
  config: CustomFieldConfig
): string {
  if (config.repeatGroup && Array.isArray(value)) {
    return `${value.length} ${value.length === 1 ? 'item' : 'itens'}`;
  }
  return formatCustomFieldValue(value, config.fieldType);
}

function renderUnknownField(key: string, value: any): React.ReactNode {
  if (Array.isArray(value)) {
    return (
      <div key={key} className="col-span-full">
        <span className="text-xs text-muted-foreground block">{key}</span>
        <div className="space-y-2 mt-1">
          <span className="text-sm font-medium text-foreground">
            {value.length} {value.length === 1 ? 'item' : 'itens'}
          </span>
          {value.map((item, idx) => (
            <div key={idx} className="ml-4 p-2 bg-muted/50 rounded-ds-card border border-border">
              <span className="text-xs font-medium text-muted-foreground mb-1 block">Item {idx + 1}</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {typeof item === 'object' && item !== null
                  ? Object.entries(item).map(([subKey, subVal]) => (
                      <div key={subKey} className="text-sm">
                        <span className="text-muted-foreground">{subKey}: </span>
                        <span className="text-foreground">{formatCustomFieldValue(subVal, 'text')}</span>
                      </div>
                    ))
                  : <span className="text-sm text-foreground">{String(item)}</span>
                }
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div key={key}>
      <span className="text-xs text-muted-foreground block">{key}</span>
      <span className="text-sm text-foreground font-medium">
        {typeof value === 'boolean' ? (value ? 'Sim' : 'Não') : formatCustomFieldValue(value, 'text')}
      </span>
    </div>
  );
}

const CustomFieldsDetail: React.FC<CustomFieldsDetailProps> = ({
  customFields,
  fieldsConfig,
}) => {
  if (!customFields || Object.keys(customFields).length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Nenhum campo personalizado preenchido.</p>
    );
  }

  const sortedFields = [...fieldsConfig].sort((a, b) => a.order - b.order);
  const configuredKeys = new Set(fieldsConfig.map(f => f.fieldKey));
  const unconfiguredKeys = Object.keys(customFields).filter(k => !configuredKeys.has(k));

  return (
    <div className="space-y-3">
      {sortedFields.map((config) => {
        const value = customFields[config.fieldKey];

        if (config.repeatGroup) {
          return (
            <div key={config.fieldKey}>
              {renderRepeatGroupDetail(value, config)}
            </div>
          );
        }

        return (
          <div key={config.fieldKey} className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">{config.label}</span>
            <span className="text-sm text-foreground font-medium">
              {formatCustomFieldValue(value, config.fieldType)}
            </span>
          </div>
        );
      })}
      {unconfiguredKeys.map((key) => renderUnknownField(key, customFields[key]))}
    </div>
  );
};

export default CustomFieldsDetail;
